import React, { useMemo, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

type Bucket = {
  label: string;
  counts: { low: number; medium: number; high: number; critical: number; info?: number; none?: number };
};

export default function VulnerabilityTrendsChart({
  buckets,
  buckets7d
}: {
  buckets: Bucket[] | undefined;
  buckets7d?: Bucket[] | undefined;
}) {
  const canvasRef = useRef<any>(null);

  const [range, setRange] = React.useState<"30d" | "7d">("30d");
  const [severity, setSeverity] = React.useState<"All" | "Info" | "Low" | "Medium" | "High" | "Critical">("All");
  const activeBuckets = range === "30d" ? (buckets || []) : (buckets7d || []);
  const labels = activeBuckets.map(b => b.label);

  const buildGradient = (ctx: CanvasRenderingContext2D, color: string) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
    gradient.addColorStop(0, color + "CC"); // ~80% opacity
    gradient.addColorStop(1, color + "14"); // ~8% opacity
    return gradient;
  };

  const data = useMemo(() => {
    const ctx = canvasRef.current?.ctx as CanvasRenderingContext2D | undefined;
    const low = activeBuckets.map(b => b.counts.low || 0);
    const medium = activeBuckets.map(b => b.counts.medium || 0);
    const high = activeBuckets.map(b => b.counts.high || 0);
    const critical = activeBuckets.map(b => b.counts.critical || 0);
    const info = activeBuckets.map(b => b.counts.info || b.counts.none || 0);

    const base = {
      labels,
      datasets: [] as any[]
    };

    const dsCritical = {
      label: "Critical",
      data: critical,
      borderColor: "#EF4444",
      backgroundColor: ctx ? buildGradient(ctx, "#EF4444") : "#EF4444CC",
      tension: 0.3,
      fill: true,
      pointRadius: 0,
      stack: "stack1"
    };
    const dsHigh = {
      label: "High",
      data: high,
      borderColor: "#F97316",
      backgroundColor: ctx ? buildGradient(ctx, "#F97316") : "#F97316CC",
      tension: 0.3,
      fill: true,
      pointRadius: 0,
      stack: "stack1"
    };
    const dsMedium = {
      label: "Medium",
      data: medium,
      borderColor: "#F2B200",
      backgroundColor: ctx ? buildGradient(ctx, "#F2B200") : "#F2B200CC",
      tension: 0.3,
      fill: true,
      pointRadius: 0,
      stack: "stack1"
    };
    const dsLow = {
      label: "Low",
      data: low,
      borderColor: "#22C55E",
      backgroundColor: ctx ? buildGradient(ctx, "#22C55E") : "#22C55ECC",
      tension: 0.3,
      fill: true,
      pointRadius: 0,
      stack: "stack1"
    };
    const dsInfo = {
      label: "Info",
      data: info,
      borderColor: "#3B82F6",
      backgroundColor: ctx ? buildGradient(ctx, "#3B82F6") : "#3B82F6CC",
      tension: 0.3,
      fill: true,
      pointRadius: 0,
      stack: "stack1"
    };

    if (severity === "All") {
      base.datasets = [dsCritical, dsHigh, dsMedium, dsLow, dsInfo];
    } else if (severity === "Critical") base.datasets = [dsCritical];
    else if (severity === "High") base.datasets = [dsHigh];
    else if (severity === "Medium") base.datasets = [dsMedium];
    else if (severity === "Low") base.datasets = [dsLow];
    else if (severity === "Info") base.datasets = [dsInfo];

    return base;
  }, [activeBuckets, labels, severity]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" as const, intersect: false },
    plugins: {
      legend: { display: true, position: "bottom" as const },
      tooltip: { enabled: true }
    },
    scales: {
      x: { stacked: severity === "All" },
      y: {
        stacked: severity === "All",
        beginAtZero: true,
        title: { display: true, text: "Number of Vulnerabilities" },
        ticks: {
          stepSize: 1,
          callback: function (value: any) {
            return Number.isInteger(value) ? value : '';
          }
        }
      }
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 h-[480px] flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-800">Vulnerability Trends</h3>
        <div className="flex items-center gap-2">
          <select
            value={range}
            onChange={e => setRange(e.target.value as any)}
            className="rounded-md px-4 py-2.5 text-sm appearance-none bg-white pr-8"
            style={{
              borderColor: "#D1D5DB",
              color: "#6B7280",
              borderWidth: "1px",
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 9L2 5h8z'/%3E%3C/svg%3E")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 0.75rem center",
              backgroundSize: "12px 12px"
            }}
          >
            <option value="30d">Last 30 Days</option>
            <option value="7d">Last 7 Days</option>
          </select>
          <select
            value={severity}
            onChange={e => setSeverity(e.target.value as any)}
            className="rounded-md px-4 py-2.5 text-sm appearance-none bg-white pr-8"
            style={{
              borderColor: "#D1D5DB",
              color: "#6B7280",
              borderWidth: "1px",
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 9L2 5h8z'/%3E%3C/svg%3E")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 0.75rem center",
              backgroundSize: "12px 12px"
            }}
          >
            <option>All</option>
            <option>Info</option>
            <option>Low</option>
            <option>Medium</option>
            <option>High</option>
            <option>Critical</option>
          </select>
        </div>
      </div>
      <div className="h-[380px]">
        {/* @ts-ignore */}
        <Line ref={canvasRef as any} data={data} options={options} />
      </div>
    </div>
  );
}


